<!--
  Component: ElementOverallRating

  This component creates an overall Framework Element rating section inside the
  tabbed content section.

  Includes:
  - Large VizFourBarRating for the current Framework element, and matching
      text description
  - Descriptive text about the element that varies based on element and
      selected school type (from store).

  Props:
  - elementCode - two-letter abbreviation: IP, SS, RF
-->

<template>
  <div class="element-overall-rating">
    <div class="header-wrapper">
      <!--header height changes when it breaks into multiple lines, we
      need something to match its height on the right to align the legend
      vertically
      -->
      <h3>{{ sectionHeading }}</h3>
      <div v-if="!isSelectedSchoolPK" class="header-filler"/>
    </div>
    <div class="content-wrapper">
      <div class="rating-content">
        <div v-if="!isSelectedSchoolPK" class="rating-col">
          <template v-if="!isCovidYear">
            <div class="rating-description">{{ elementRatingDescription }}</div>
            <VizFourBarRating
              class="large"
              :x-padding="3"
              :y-padding="0"
              :hollow-style="true"
              :rating="elementRating"
            />
          </template>
          <template v-else>
            <div class="na-description">N/A</div>
          </template>
        </div>
        <div class="description-col-wrapper">
          <div class="description-col">
            <div class="description-col-block">
              <div class="description main">
                <template v-if="elementCode=='RF'">
                  The <b>Relationships with Families</b> {{ ['EC', 'D75'].includes(selectedSchoolType) ? 'section' : 'rating' }} looks at how well the school forms effective partnerships with families and whether those relationships are based on trust and respect.
                </template>
                <template v-else-if="elementCode=='SS'">
                  The <b>Safety and School Climate</b> {{ ['EC', 'D75'].includes(selectedSchoolType) ? 'section' : 'rating' }} looks at how well the school establishes a culture where students feel safe, challenged to grow, and supported to meet high expectations; how well school leadership inspires the school community; and how well teachers participate in the continuous improvement of the school community.
                </template>
                <template v-else-if="elementCode=='IP'">
                  <template v-if="selectedSchoolType === 'D75'">
                    The <b>Instruction and Performance</b> section looks at this school’s State and local assessment results and survey questions about instruction from the <TheSchoolSurveyLink :panoramaURL="panoramaURL"/>.
                  </template>
                  <template v-else-if="selectedSchoolType === 'EC'">
                    The <b>Instruction and Performance</b> section looks at this school’s State test results, when applicable, and survey questions about instruction from the <TheSchoolSurveyLink :panoramaURL="panoramaURL"/>.
                  </template>
                  <template v-else-if="['HS', 'HST'].includes(selectedSchoolType)">
                    The <b>Instruction and Performance</b> rating looks at students’ college and career readiness, progress towards graduation, graduation rates, and survey questions about instruction from the <TheSchoolSurveyLink :panoramaURL="panoramaURL"/>.
                  </template>
                  <template v-else>
                    The <b>Instruction and Performance</b> rating looks at this school’s State test results, including student growth and performance; how students performed in core courses; how well students are prepared for {{ selectedSchoolType === 'ES' ? 'middle' : 'high' }} school; and survey questions about instruction from the <TheSchoolSurveyLink :panoramaURL="panoramaURL"/>.
                  </template>
                </template>
              </div>

              <div v-if="!isSelectedSchoolPK" class="description sub">
                <template v-if="['ES', 'MS', 'K-8', 'HS', 'HST'].includes(selectedSchoolType)">
                  <template v-if="elementCode === 'IP'">
                    An entire list of metrics contributing to the rating can be found in the <TheScoringGuideLink :scoringGuideURL="scoringGuideURL" :isEmbargoed="isEmbargoed" />.
                    <template v-if="hasQRReview">
                      For a detailed report that evaluates how well this school is organized to support student learning and achievement, please see the latest <TheQRReportLink :qualityReviewURL="qualityReviewURL"/>.
                    </template>
                  </template>
                  <template v-else-if="elementCode === 'SS'">
                    This section rating is a combination of student attendance and <TheSchoolSurveyLink :panoramaURL="panoramaURL"/> measures, including those presented here. An entire list of metrics contributing to the rating can be found in the <TheScoringGuideLink :scoringGuideURL="scoringGuideURL" :isEmbargoed="isEmbargoed" />.
                  </template>
                  <template v-else-if="elementCode === 'RF'">
                    This section rating is based on the results of the <TheSchoolSurveyLink :panoramaURL="panoramaURL"/>.
                  </template>
                </template>
                <!-- below is for EC and D75 -->
                <template v-else>
                  <template v-if="elementCode === 'IP' && hasQRReview">For a detailed report that evaluates how well this school is organized to support student learning and achievement, please see the latest <TheQRReportLink :qualityReviewURL="qualityReviewURL"/>.</template>
                </template>
              </div>

              <!-- Additional footnote below sub description for all school types -->
              <div class="footnotes">
                <FrameworkRatingFootnote v-if="isECorD75SchoolType" :footnote-type="FR_FOOTNOTE_TYPES.EC_OR_D75"/>
                <FrameworkRatingFootnote v-if="isNewOrPhaseoutSchool" :footnote-type="FR_FOOTNOTE_TYPES.NEW_OR_PHASEOUT"/>
                <template v-if="elementCode=='IP'">
                  <FrameworkRatingFootnote v-if="displayHighOptOut" :footnote-type="FR_FOOTNOTE_TYPES.OPT_OUT"/>
                  <FrameworkRatingFootnote v-if="displayNoGraduatingClass" :footnote-type="FR_FOOTNOTE_TYPES.NO_GRADUATING_CLASS"/>
                </template>
                <template v-if="elementCode == 'SS'">
                  <FrameworkRatingFootnote v-if="displaySuppressSS" :footnote-type="FR_FOOTNOTE_TYPES.SUPPRESS_RF_OR_SS"/>
                </template>
                <template v-if="elementCode == 'RF'">
                  <FrameworkRatingFootnote v-if="displaySuppressRF" :footnote-type="FR_FOOTNOTE_TYPES.SUPPRESS_RF_OR_SS"/>
                </template>
              </div>
            </div>
          </div>
          <div v-if="!isSelectedSchoolPK" class="legend-block">
            <div class="key-title">Key:</div>
            <FrameworkRatingsLegend/>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue'
import { mapGetters } from 'vuex'
import Component from 'vue-class-component'

import { NS_REPORT_GROUP, NS_CURRENT_REPORT } from '../const'
import { frElementNameForCode, valIsMissing } from '@/utils/data'

import LinkExternalUrl from '@/components/LinkExternalUrl'
import VizFourBarRating, { descriptionForRating } from '@/components/VizFourBarRating'
import FrameworkRatingFootnote, { FR_FOOTNOTE_TYPES } from '../FrameworkRatingFootnote'
import FrameworkRatingsLegend from '../FrameworkRatingsLegend'

// Local Component definitions
const TheScoringGuideLink = {
  props: {
    scoringGuideURL: String,
    isEmbargoed: Boolean
  },
  components: { LinkExternalUrl },
  template: '<span v-if="isEmbargoed">Scoring Guide PDF (coming soon)</span><span v-else><LinkExternalUrl :url="scoringGuideURL">SQR Scoring Guide (PDF)</LinkExternalUrl></span>'
}
const TheSchoolSurveyLink = {
  props: { panoramaURL: String },
  components: { LinkExternalUrl },
  template: '<span><LinkExternalUrl :url="panoramaURL">NYC School Survey</LinkExternalUrl></span>'
}
const TheQRReportLink = {
  props: { qualityReviewURL: String },
  components: { LinkExternalUrl },
  template: '<span><LinkExternalUrl :url="qualityReviewURL">Quality Review Report (PDF)</LinkExternalUrl></span>'
}

@Component({
  props: {
    elementCode: String
  },
  computed: {
    ...mapGetters(NS_REPORT_GROUP, [
      'schoolYearRange'
    ]),
    ...mapGetters(NS_CURRENT_REPORT, [
      'selectedSchoolType',
      'isSelectedSchoolPK',
      'isSelectedSchoolCharter',
      'isSchoolDataLoaded',
      'elementRatingForCode',
      'noRatingTextForCode',
      'isEmbargoed',
      'scoringGuideURL',
      'hasQRReview',
      'qualityReviewURL',
      'panoramaURL',
      'isCovidYear',
      'isECorD75SchoolType',
      'isNewOrPhaseoutSchool',
      'isHighOptOutSchool',
      'isNoGraduatingClassSchool',
      'suppress_ss',
      'suppress_rf'
    ])
  },
  components: {
    TheScoringGuideLink,
    TheSchoolSurveyLink,
    TheQRReportLink,
    VizFourBarRating,
    FrameworkRatingsLegend,
    FrameworkRatingFootnote
  }
})
export default class ElementOverallRating extends Vue {
  FR_FOOTNOTE_TYPES = FR_FOOTNOTE_TYPES

  /* Getters */

  get sectionHeading () {
    return this.isSelectedSchoolPK ? this.elementName : `Overall Rating for ${this.elementName}`
  }
  get elementName () {
    return frElementNameForCode(this.elementCode)
  }
  get elementRating () {
    return this.elementRatingForCode(this.elementCode)
  }
  get isMissingRating () {
    return valIsMissing(this.elementRating)
  }
  get elementRatingDescription () {
    // Show blank if data not loaded yet, No Rating if data is missing
    const noRatingText = this.noRatingTextForCode(this.elementCode)
    return this.isSchoolDataLoaded && this.isMissingRating
      ? noRatingText : descriptionForRating(this.elementRating)
  }
  get displayHighOptOut () {
    return this.isHighOptOutSchool && !this.isNewOrPhaseoutSchool && !this.isECorD75SchoolType
  }

  get displayNoGraduatingClass () {
    return this.isNoGraduatingClassSchool && !this.isNewOrPhaseoutSchool && !this.isECorD75SchoolType
  }

  get displaySuppressSS () {
    return this.suppress_ss && !this.isNewOrPhaseoutSchool && !this.isECorD75SchoolType
  }

  get displaySuppressRF () {
    return this.suppress_rf && !this.isNewOrPhaseoutSchool && !this.isECorD75SchoolType
  }
}
</script>

<style lang="scss">
</style>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss" scoped>
@import 'src/styles/const.scss';
@import '../css/const.scss';

$legend-width: 185px;
$legend-border-width: 1px;
$content-margin-top: 18px;
$right-legend-padding-top: 11px;
$right-legend-padding-left: 20px;
$right-legend-margin-top: -$content-margin-top;
$right-legend-margin-left: 20px;
$bottom-legend-padding-top: 0;
$bottom-legend-padding-left: 0;
$bottom-legend-margin-top: 0;
$bottom-legend-margin-left: 0px;
$bottom-legend-item-margin-right-lg: 25px;
$bottom-legend-item-margin-right-sm: 20px;

.header-wrapper {
  display: flex;
  h3 {
    flex: 1;
    margin-bottom: 0;
  }
  .header-filler {
    flex: 0 0 $legend-width + 2 * $legend-border-width;
    padding-left: $right-legend-padding-left;
    margin-left: $right-legend-margin-left;
  }
}

.content-wrapper {
  .rating-content {
    display: flex;
    padding-top: $content-margin-top;
    > div {
      display: inline-block;
    }
    .rating-col {
      margin-right: 24px;
      .rating-description {
        min-height: 24px;
        font-size: 20px;
        font-weight: bold;
        margin-bottom: 4px;
      }
      .viz-four-bar-rating {
        display: block;
      }
    }

    .na-description {
      font-size: 30px;
      font-weight: bold;
      min-width: 104px;
      text-align: center;
      padding-top: 8px;
      color: $alert-red-color;
    }
    .description-col-wrapper {
      display: flex;
    }
    .framework-rating-footnote.footnote {
      padding-top: 12px;
    }
  }

  .legend-block {
    margin: $right-legend-margin-top 0 0 $right-legend-margin-left;
    border: $legend-border-width solid #555555;
    padding: $content-margin-top 0 $content-margin-top 0;
    height: fit-content;
  }
  .key-title {
    display: block;
    color: #555;
    font-weight: bold;
    padding-left: 18px;
  }
  .framework-ratings-legend {
    width: $legend-width;
    display: block;
    /deep/ .legend-item {
      margin-bottom: 5px;
      .viz-four-bar-rating {
        display: inline-block;
      }
      .name {
        display: inline-block;
        max-width: 140px;
        color: #555555;
      }
    }
  }
  .framework-rating-footnote.footnote {
    padding-top: 12px;
  }
}

.description.main {
  font-size: 17px;
  @media print {
    font-size: 15px;
  }
}
.description.sub {
  margin-bottom: 0px;
  span.covid-sub {
    font-size: 17px;
    font-weight: bold;
    color: $alert-red-color;
  }
}

/* Responsive rules */
/* upto 956px */
@media screen and (max-width: $screen-width-2-column-small) {
  .content-wrapper {
    .legend-block {
      padding-left: $right-legend-padding-left;
      }
    .key-title {
      padding-left: 0;
    }
  }
}
/* upto 644 */
@media screen and (max-width: $screen-width-1-column-large) {
  .header-filler {
    display: none;
  }
  .content-wrapper {
    .rating-content .description-col-wrapper {
      display: block;
    }
    .legend-block {
      margin: $bottom-legend-margin-top 0 0 $bottom-legend-margin-left;
      padding: 15px 0 0 $bottom-legend-padding-left;
      border: none;
    }
    .framework-ratings-legend {
      width: 100%;
      height: auto;
      padding: $bottom-legend-padding-top 0 0 $bottom-legend-padding-left;
      /deep/ .legend-item {
        display: inline-block;
        vertical-align: top;
        &:not(:last-child) {
          margin-right: $bottom-legend-item-margin-right-lg;
        }
        .viz-four-bar-rating {
          display: block;
        }
        .name {
          display: block;
          max-width: 72px;
        }
      }
    }
  }
}
/* upto 538 */
@media screen and (max-width: $screen-width-1-column-medium) {
  .description.main {
    font-size: 15px;
  }
  .content-wrapper {
    .framework-ratings-legend {
      /deep/ .legend-item {
        &:not(:last-child) {
          /* Need to lower this or else legend will wrap before 456px cutoff */
          margin-right: $bottom-legend-item-margin-right-sm;
        }
      }
    }
  }
}

/* upto 456 */
@media screen and (max-width: $screen-width-1-column-small) {
  .content-wrapper {
    .key-title {
      margin-left: 0;
    }
    .framework-ratings-legend {
      margin-left: 0;
      /deep/ .legend-item {
        &:not(:last-child) {
          margin-right: $bottom-legend-item-margin-right-lg;
        }
      }
    }
    .rating-content {
      display: block;
      > div {
        display: block;
      }
      .rating-col {
        margin-bottom: 12px;
      }
      .rating-col .rating-description,
      .rating-col .viz-four-bar-rating.no-rating {
        display: inline-block;
      }
    }
  }
}

/* above 300px and below 456px */
@media screen and (max-width: $min-width-col) {
  .content-wrapper {
    .framework-ratings-legend {
      /deep/ .legend-item {
        &:not(:last-child) {
          margin-right: $bottom-legend-item-margin-right-sm;
        }
      }
    }
  }

}

</style>



// WEBPACK FOOTER //
// src/components/sqs/2024/tab-mods/ElementOverallRating.vue